import GameConfig from "../core/data/GameConfig";
import PlayerData from "../PlayerData";
import { EnvironmentData } from "./ChunkSpawner";
import { GameCompleteState } from "./GameCompleteState";
import { GameOverState } from "./GameOverState";
import { GameState } from "./GameState";
import { Hazard } from "./Hazard";
import { CharacterUnlockData } from "./MissionsState";
import { MovingObject } from "./MovingObject";
import { NPCRunner } from "./NPCRunner";
import { Player } from "./Player";
import { SetDecoLayer } from "./ScrollingBG";

export enum Environment {
	BEDROCK = 0,
	METROPOLIS = 1,
	HAUNTEDHOUSE = 2,
	GOTHAM = 3,
	ENDING = 4,
}

export class FlyState extends GameState {

	inputObj: Phaser.GameObjects.Image;
	spawnTimerMove = 0.05;
	spawnTimerNPC = 1;
	spawnTimerHazard: number;
	progressStart = 0;
	progressEnd = 0;
	environmentTimer = 0;

	characterNames = [
		"bugs",
		"lola",
		"daffy",
		"tweety",
		"porky",
		"roadrunner",
		"sylvester",
		"taz",
		"wile",
	]

	currentEnvironment: Environment;

	start(): void {
		const inputObj = this.inputObj = this.scene.add.image(0, 0, "whiteSquare");
		inputObj.setSize(this.scene.viewport.width * 5, this.scene.viewport.height * 5);
		inputObj.setInteractive();
		inputObj.alpha = 0.001;
		this.scene.uiContainer.add(inputObj);
		this.scene.uiContainer.sendToBack(inputObj);
		inputObj.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => this.onInput());
		this.game.player.once(Player.ON_STOPPED, this.onStopped, this);
		this.game.player.on(Player.ON_PASSED_WALL, this.onPassedWall, this);
		this.progressEnd = this.game.chunkSpawner.xPos + this.game.chunkSpawner.getEnvironmentWidth();
		this.scene.cameraController.targetOffsetX = 300;
		this.currentEnvironment = Environment.BEDROCK;
		this.spawnTimerHazard = GameConfig.get<number>("hazardSpawnTime");
	}

	update(dt: number): void {
		this.environmentTimer += dt;

		const vel = this.game.player.planckBody.velocity.x;

		const progress = (vel - 5) / (30 - 5);
		let scale = Phaser.Math.Linear(0.9, 0.7, progress);
		scale = Phaser.Math.Clamp(scale, 0.7, 1);
		this.scene.cameraController.targetScale = scale;

		const leftBound = this.scene.cameraController.getLeftBound();
		const rightBound = this.scene.cameraController.getRightBound();
		const groundY = 1080 - this.game.chunkSpawner.groundHeight / 2;

		if (this.currentEnvironment != Environment.ENDING) {
			this.spawnTimerNPC -= dt;
			if (this.spawnTimerNPC <= 0) {
				const objs = this.scene.objects.filter(o => o instanceof NPCRunner) as NPCRunner[];
				if (objs.length < GameConfig.get<number>("NPCMaxOnScreen")) {
					const minL = GameConfig.get<number>("NPCMinVelocityLeft");
					const maxL = GameConfig.get<number>("NPCMaxVelocityLeft");
					const minR = GameConfig.get<number>("NPCMinVelocityRight");
					const maxR = GameConfig.get<number>("NPCMaxVelocityRight");
					this.spawnTimerNPC += 0.5;

					// get valid character
					const playerIndex = PlayerData.Instance.saveData.currentCharacter;
					const playerName = (GameConfig.get("characterUnlocks") as CharacterUnlockData[])[playerIndex].name;
					const cloneArray = [];

					for (const name of this.characterNames) {
						if (!playerName.includes(name)) {
							let flag = true;
							for (const obj of objs) {
								if (obj.spineName == name) flag = false
							}
							if (flag) cloneArray.push(name);
						}
					}

					let rand = Phaser.Math.RND.between(0, cloneArray.length - 1);
					let spineName = cloneArray[rand];
					cloneArray.splice(rand, 1);

					const vel1 = Phaser.Math.RND.realInRange(minL, maxL) + this.game.player.planckBody.velocity.x;
					this.scene.addObject(new NPCRunner(this.scene, leftBound - 200, 1080 - 100, vel1, spineName));

					if (cloneArray.length > 0) {
						let rand = Phaser.Math.RND.between(0, cloneArray.length - 1);
						let spineName = cloneArray[rand];
						const vel2 = Phaser.Math.RND.realInRange(minR, maxR) + this.game.player.planckBody.velocity.x;
						this.scene.addObject(new NPCRunner(this.scene, rightBound + 200, 1080 - 100, vel2, spineName));
					}
				}
			}

			this.spawnTimerMove -= dt;
			if (this.spawnTimerMove <= 0) {
				const objs = this.scene.objects.filter(o => o instanceof MovingObject);
				const env = GameConfig.get<EnvironmentData[]>("environments")[this.currentEnvironment];
				if (objs.length < env.movingObjectMaxOnScreen) {
					const movingObjectPool = this.game.movingObjectPools[this.currentEnvironment];
					this.spawnTimerMove += 0.1;
					const vel1 = Phaser.Math.RND.realInRange(env.movingObjectMinVelocityLeft, env.movingObjectMaxVelocityLeft) + this.game.player.planckBody.velocity.x;
					const o1 = movingObjectPool.get(leftBound - 200, 1080 - 100, vel1);
					this.scene.addObject(o1, "defaultBack");
					let vel2 = Phaser.Math.RND.realInRange(env.movingObjectMinVelocityRight, env.movingObjectMaxVelocityRight) + this.game.player.planckBody.velocity.x;
					if (this.environmentTimer < 1) vel2 -= 10; // makes it easier to land on a moving object in the first second after launch
					else if (vel2 < 2) vel2 = 2;
					const o2 = movingObjectPool.get(rightBound + 180, 1080 - 100, vel2);
					this.scene.addObject(o2, "defaultBack");
				}
			}

			this.spawnTimerHazard -= dt;
			if (this.spawnTimerHazard <= 0) {
				const existingHazard = this.scene.objects.find(o => o instanceof Hazard);
				if (!existingHazard) {
					this.spawnTimerHazard += GameConfig.get<number>("hazardSpawnTime");
					const speed = GameConfig.get<number>("hazardSpeedRelativeToPlayer");
					const vel2 = Math.max(this.game.player.planckBody.velocity.x * speed, 4);
					this.scene.addObject(new Hazard(this.scene, rightBound + 200, groundY, vel2));
				}
			}
		}

		this.scene.ui.updateDistance(this.game.getPlayerDistance());
		const playerProgress = (this.game.player.x - this.progressStart) / this.progressEnd;
		this.scene.ui.updateProgress(playerProgress);
	}

	nextEnvironment(): void {
		this.game.currentEnvironment++;
		this.environmentTimer = 0;
		this.progressStart = this.game.player.x;
		this.progressEnd = this.game.chunkSpawner.getEnvironmentWidth();

		// destroy all objects that don't match the new environment
		const mobjects = this.scene.objects.filter(o => o instanceof MovingObject && o.currentEnvironment != this.game.currentEnvironment);
		mobjects.forEach(o => o.destroy());
	}

	onPassedWallMidpoint(): void {
		this.currentEnvironment = this.game.currentEnvironment + 1;
		if (this.currentEnvironment == Environment.METROPOLIS) {
			this.scene.scrollingBG.setMetropolisLayers();
		}
		if (this.currentEnvironment == Environment.HAUNTEDHOUSE) {
			this.scene.scrollingBG.setHauntedHouseLayers();
		}
		if (this.currentEnvironment == Environment.GOTHAM) {
			this.scene.scrollingBG.setGothamLayers();
		}
		if (this.currentEnvironment == Environment.ENDING) {
			this.scene.scrollingBG.setEndingLayers();
			// destroy any running objects that might be hanging around
			const classesToDestroy = [MovingObject, NPCRunner, Hazard];
			const objectsToDestroy = this.scene.objects.filter(o => classesToDestroy.some(c => o instanceof c));
			objectsToDestroy.forEach(o => o.destroy());
		}

		console.log("Environment data on creation: ", this.scene.ltGame.currentEnvironment);
		const fg = this.scene.scrollingBG.layers.find(l => l instanceof SetDecoLayer);
		if (this.scene.ltGame.currentEnvironment != Environment.GOTHAM && this.scene.ltGame.currentEnvironment != Environment.ENDING && fg) {
			let str = "billboardMetropolis";
			if (this.scene.ltGame.currentEnvironment == Environment.METROPOLIS) str = "billboardScooby";
			else if (this.scene.ltGame.currentEnvironment == Environment.HAUNTEDHOUSE) str = "billboardGotham";
			console.log("Creating billboard " + str);
			const billboard = fg.addImage(str);
			billboard.setOrigin(0, 1);
			billboard.setPosition(fg.getLeftBound().x + this.scene.viewport.width * 1.75, fg.imgYOffset);
			console.log(billboard.x, billboard.y);
		}
	}

	onPassedWall(): void {
		const chunkSpawner = this.game.chunkSpawner;
		if (this.game.currentEnvironment != chunkSpawner.environment) this.nextEnvironment();
		if (this.game.currentEnvironment == Environment.ENDING) {
			this.game.switchState(new GameCompleteState(this.game, this.scene));
		}
	}

	onInput(): void {
		this.game.player.slam();
	}

	onStopped(): void {
		this.scene.soundManager.play("characterStopped");
		this.game.switchState(new GameOverState(this.game, this.scene));
	}

	shutdown(): void {
		this.game.player.off(Player.ON_STOPPED);
		this.game.player.off(Player.ON_PASSED_WALL);
		this.inputObj.destroy();
	}

}